import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, X, Send, ArrowLeft, Link2, Paperclip, Loader2, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useLiveSync } from "@/hooks/useLiveSync";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");

export function ChatPanel({ rides = [] }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // {driver_id, driver_name}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [rideId, setRideId] = useState("none");
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/chat/threads").then((r) => {
      setThreads(r.data);
      setUnread(r.data.reduce((s, t) => s + (t.unread || 0), 0));
    }).catch(() => {});
  }, []);

  const loadMessages = useCallback((driverId) => {
    api.get(`/chat/thread/${driverId}`).then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useLiveSync((evt) => {
    if (evt?.type !== "chat_updated") return;
    loadThreads();
    if (active && evt.driver_id === active.driver_id) loadMessages(active.driver_id);
  });

  useEffect(() => {
    if (!active) return;
    loadMessages(active.driver_id);
    api.post(`/chat/thread/${active.driver_id}/read`).then(loadThreads).catch(() => {});
  }, [active, loadMessages, loadThreads]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages, open]);

  const send = async () => {
    const body = { text: text.trim() };
    if (!body.text || !active) return;
    if (rideId !== "none") body.ride_id = rideId;
    setText("");
    try {
      await api.post(`/chat/thread/${active.driver_id}`, body);
      setRideId("none");
      loadMessages(active.driver_id);
      loadThreads();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sendPhoto = async (file) => {
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/chat/thread/${active.driver_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      loadMessages(active.driver_id);
      loadThreads();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      <Button variant={open ? "default" : "outline"} onClick={() => setOpen((s) => !s)} className="gap-2 relative" data-testid="laregul-chat-toggle">
        <MessageSquare className="h-4 w-4" /> Messages
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="chat-unread-badge">
            {unread}
          </span>
        )}
      </Button>

      {open && (
        <aside
          className="no-print fixed top-0 right-0 z-50 h-screen w-full sm:w-[380px] bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
          data-testid="chat-panel"
        >
          <header className="h-14 px-3 flex items-center gap-2 border-b border-border shrink-0">
            {active ? (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setActive(null)} data-testid="chat-back">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{active.driver_name}</p>
                  <p className="text-[11px] text-muted-foreground">Chauffeur</p>
                </div>
              </>
            ) : (
              <p className="font-display font-bold text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Messages chauffeurs</p>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 ml-auto" onClick={() => setOpen(false)} data-testid="chat-close">
              <X className="h-4 w-4" />
            </Button>
          </header>

          {!active && (
            <div className="flex-1 overflow-y-auto divide-y divide-border" data-testid="chat-threads">
              {threads.length === 0 && <p className="p-4 text-sm text-muted-foreground">Aucun chauffeur enregistré.</p>}
              {threads.map((t) => (
                <button
                  key={t.driver_id}
                  type="button"
                  onClick={() => setActive({ driver_id: t.driver_id, driver_name: t.driver_name })}
                  className="w-full text-left px-3 py-3 hover:bg-secondary transition-colors flex items-start gap-2"
                  data-testid={`chat-thread-${t.driver_id}`}
                >
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                    {(t.driver_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{t.driver_name}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{hhmm(t.last_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                      {t.last_sender === "company" && (
                        t.last_read_driver
                          ? <CheckCheck className="h-3 w-3 shrink-0 text-emerald-600" data-testid={`chat-thread-seen-${t.driver_id}`} />
                          : <Check className="h-3 w-3 shrink-0" data-testid={`chat-thread-sent-${t.driver_id}`} />
                      )}
                      <span className="truncate">
                        {t.last_text ? `${t.last_sender === "company" ? "Vous : " : ""}${t.last_text}` : "Démarrer la conversation"}
                      </span>
                    </p>
                  </div>
                  {t.unread > 0 && <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] shrink-0">{t.unread}</Badge>}
                </button>
              ))}
            </div>
          )}

          {active && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Aucun message. Écrivez le premier.</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "company" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "company" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                      {m.ride_label && (
                        <span className={`block text-[10px] mb-1 flex items-center gap-1 ${m.sender === "company" ? "opacity-80" : "text-muted-foreground"}`}>
                          <Link2 className="h-3 w-3" /> {m.ride_label}
                        </span>
                      )}
                      {m.photo_doc_id ? (
                        <a href={`${API_BASE}/chat/photo/${m.photo_doc_id}`} target="_blank" rel="noreferrer" data-testid={`chat-photo-${m.id}`}>
                          <img src={`${API_BASE}/chat/photo/${m.photo_doc_id}`} alt="Pièce jointe" className="rounded-lg max-h-60 w-auto" />
                        </a>
                      ) : (
                        <span className="whitespace-pre-wrap break-words">{m.text}</span>
                      )}
                      <span className={`block text-[10px] mt-0.5 text-right ${m.sender === "company" ? "opacity-70" : "text-muted-foreground"}`}>{hhmm(m.created_at)}</span>
                      {m.sender === "company" && (
                        <span className="flex items-center justify-end gap-1 text-[10px] opacity-80" data-testid={`chat-receipt-${m.id}`}>
                          {m.read_driver ? (
                            <><CheckCheck className="h-3 w-3" /> Vu{m.read_driver_at ? ` à ${hhmm(m.read_driver_at)}` : ""}</>
                          ) : (
                            <><Check className="h-3 w-3" /> Envoyé</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              <div className="border-t border-border p-2 space-y-2 shrink-0">
                <Select value={rideId} onValueChange={setRideId}>
                  <SelectTrigger className="h-8 text-xs" data-testid="chat-ride-select">
                    <SelectValue placeholder="Citer une course (facultatif)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune course citée</SelectItem>
                    {rides.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{`${r.pec_time || r.time} · ${r.patient_name}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => sendPhoto(e.target.files?.[0])} data-testid="chat-file" />
                  <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-photo-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </Button>
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Votre message…"
                    data-testid="chat-input"
                  />
                  <Button size="icon" onClick={send} disabled={!text.trim()} data-testid="chat-send">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </aside>
      )}
    </>
  );
}
